import { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

function Helix() {
  const ref = useRef();
  const { positionsA, positionsB, connectors } = useMemo(() => {
    const count = 60;
    const turns = 3;
    const height = 8;
    const radius = 1.2;
    const a = [];
    const b = [];
    const conn = [];
    for (let i = 0; i < count; i++) {
      const t = i / (count - 1);
      const y = -height / 2 + t * height;
      const angle = t * turns * Math.PI * 2;
      a.push({ pos: [Math.cos(angle) * radius, y, Math.sin(angle) * radius] });
      b.push({ pos: [Math.cos(angle + Math.PI) * radius, y, Math.sin(angle + Math.PI) * radius] });
      if (i % 2 === 0) {
        conn.push({
          a: [Math.cos(angle) * radius, y, Math.sin(angle) * radius],
          b: [Math.cos(angle + Math.PI) * radius, y, Math.sin(angle + Math.PI) * radius],
        });
      }
    }
    return { positionsA: a, positionsB: b, connectors: conn };
  }, []);

  useFrame(({ clock }) => {
    if (ref.current) ref.current.rotation.y = clock.getElapsedTime() * 0.35;
  });

  return (
    <group ref={ref}>
      {positionsA.map((p, i) => (
        <mesh key={`a${i}`} position={p.pos}>
          <sphereGeometry args={[0.14, 16, 16]} />
          <meshStandardMaterial
            color="#00F0FF"
            emissive="#00F0FF"
            emissiveIntensity={1.5}
            roughness={0.3}
          />
        </mesh>
      ))}
      {positionsB.map((p, i) => (
        <mesh key={`b${i}`} position={p.pos}>
          <sphereGeometry args={[0.14, 16, 16]} />
          <meshStandardMaterial
            color="#D900FF"
            emissive="#D900FF"
            emissiveIntensity={1.5}
            roughness={0.3}
          />
        </mesh>
      ))}
      {connectors.map((c, i) => {
        const start = new THREE.Vector3(...c.a);
        const end = new THREE.Vector3(...c.b);
        const mid = start.clone().add(end).multiplyScalar(0.5);
        const dir = end.clone().sub(start);
        const length = dir.length();
        const quat = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          dir.clone().normalize()
        );
        return (
          <mesh
            key={`c${i}`}
            position={mid.toArray()}
            quaternion={quat.toArray()}
          >
            <cylinderGeometry args={[0.025, 0.025, length, 8]} />
            <meshBasicMaterial color="#00FF66" transparent opacity={0.55} />
          </mesh>
        );
      })}
    </group>
  );
}

export default function DNAHelix() {
  return (
    <Canvas camera={{ position: [0, 0, 7], fov: 45 }} dpr={[1, 1.8]}>
      <ambientLight intensity={0.4} />
      <pointLight position={[5, 0, 5]} color="#00F0FF" intensity={2} />
      <pointLight position={[-5, 0, 5]} color="#D900FF" intensity={2} />
      <Suspense fallback={null}>
        <Helix />
      </Suspense>
    </Canvas>
  );
}
